import { Button, Heading } from '@mm/components';
import { CloseCircleIcon, TrashIcon } from '@mm/icons';
import ButtonModel, {
  ButtonType,
  GenericButtonModel,
  OptInButtonConfig,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import TagModel from '@mm/state/model/TagModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as RedInfoIcon } from 'assets/red-info.svg';
import classnames from 'classnames';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ComponentInputElementTypes } from '../../../../../../editor/components/AttributeContainingTextInput/AttributeContainingTextInput';
import { colors } from '../../../../../../editor/features/beta/styles/colors';
import { ButtonTagsForm } from '../../../../../../editor/features/build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import { DialogueSelectForm } from '../../../../../../editor/features/campaigns/components/DialogueSelectForm/DialogueSelectForm';
import { useIsLeadGeneratorBuilderRoute } from '../../../../../../modules/FreeLeadGenerator/use-is-lead-generator-builder-route';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import {
  FormRef,
  getUpdatedTextWithEmoji,
} from '../../../../../components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../components/EmojiPicker/EmojiPicker';
import Hint from '../../../../../components/Hint/Hint';
import { InputWithEmoji } from '../../../../../components/InputWithEmoji/InputWithEmoji';
import { DropdownOption } from '../../../../../components/PrimarySelect/PrimarySelect';
import { PrimaryToolTip } from '../../../../../components/PrimaryToolTip/PrimaryToolTip';
import TextInput from '../../../../../components/TextInput';
import { successToast } from '../../../../../helpers/toast';
import { useButtonHandlers } from '../../../../../helpers/widget-helpers/use-button-handlers';
import { useQQWidgetHandlers } from '../../../../../helpers/widget-helpers/use-qq-widget-handlers';
import { PathGraphContext } from '../../../../common/PathGraph/PathGraph';
import { QuestionButtonImageUpload } from '../QuestionButtonImageUpload/QuestionButtonImageUpload';
import './QuestionButtonActiveTabArea.pcss';

type Props = {
  quickQuestion: WidgetModel;
  editMode: boolean;
  button: ButtonModel;
  setSelectedButton: (selectedButton: number | undefined) => void;
  localButtonName: string;
  setLocalButtonName: (localButtonName: string) => void;
  dialogueId?: number;
  smsOptinMode?: boolean;
};

const useInputRef = (editMode: boolean) => {
  const [inputRef, setInputRef] = useState<FormRef | null>(null);
  const memoizedSetInputRef = useCallback(
    (ref: ComponentInputElementTypes) => {
      setInputRef(ref);
    },
    [setInputRef],
  );

  useEffect(() => {
    if (editMode && inputRef) {
      inputRef.focus();
    }
  }, [editMode, inputRef]);

  return { inputRef, memoizedSetInputRef };
};

const MAX_INPUT_LENGTH = 20;

function buttonIsOptin(
  button: ButtonModel,
): button is GenericButtonModel<OptInButtonConfig> {
  return 'kind' in button.config && 'smsResponse' in button.config;
}

export const QuestionButtonActiveTabArea = (props: Props) => {
  const {
    quickQuestion,
    setSelectedButton,
    button,
    editMode,
    localButtonName,
    setLocalButtonName,
    dialogueId,
    smsOptinMode = false,
  } = props;
  const { inputRef, memoizedSetInputRef } = useInputRef(editMode);
  const {
    updateButtonText,
    deleteButton,
    updateSmsResponse,
    updateAssociatedDialogue,
  } = useQQWidgetHandlers();
  const buttonHandlers = useButtonHandlers();
  const kind = buttonIsOptin(button) ? button.config.kind : '';
  const [smsResponse, setSmsResponse] = useState(
    buttonIsOptin(button) ? button.config.smsResponse : '',
  );
  const tags = button.tags || [];
  const pages = useSelector((state: StoreState) => state.page.entities);

  const { setActiveNode, removeNodeAndSuccessors, addWidgetButtonsToGraph } =
    useContext(PathGraphContext);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const smsActivated = useSelector(hasSMSNumberSelector);
  const { pageId } = button.config as PageButtonConfig;
  const noOption = { name: 'No', id: -1, pageGroupId: -1, kind: null as any };
  const selectedPage = pages.find(p => p.id === pageId) || noOption;

  const onDropDownChange = async (option?: DropdownOption) => {
    const pageId = option?.id === -1 || !option ? undefined : Number(option.id);

    const response = await updateAssociatedDialogue(
      quickQuestion,
      button,
      pageId,
    );
    setSelectedButton(undefined);

    const config = response?.config as any;

    if (response.id !== button.id || config?.pageId !== config.pageId) {
      removeNodeAndSuccessors?.({
        id: button.id,
        type: 'button',
        actionType: 'delete',
        parentId: quickQuestion.id,
        parentType: 'widget',
      });

      addWidgetButtonsToGraph?.(quickQuestion.id, [response]);
    }

    setActiveNode?.({
      id: response.id,
      type: 'button',
      dialogueId: pageId,
      action: pageId ? 'open' : 'close',
    });
  };

  const updateInsertEmojiQuestion = (emoji: string) => {
    if (inputRef) {
      const newText = getUpdatedTextWithEmoji(localButtonName, emoji, inputRef);
      setLocalButtonName(newText);
      updateButtonText(quickQuestion, button, newText);
    }
  };

  const onSmsInputBlur = () => {
    if (buttonIsOptin(button)) {
      if (button.config.smsResponse !== smsResponse) {
        updateSmsResponse(quickQuestion, button, smsResponse);
      }
    }
  };

  const onInputBlur = () => {
    if (button.name !== localButtonName) {
      updateButtonText(quickQuestion, button, localButtonName);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setLocalButtonName(e.target.value);
  };

  const updateTags = (button: ButtonModel, tags: TagModel[]) => {
    buttonHandlers.updateButton(quickQuestion, { ...button, tags });
  };

  const onClickDelete = () => {
    setActiveNode?.({
      id: button.id,
      type: 'button',
      action: 'close',
    });
    removeNodeAndSuccessors?.({
      id: button.id,
      type: 'button',
      actionType: 'delete',
      parentId: quickQuestion.id,
      parentType: 'widget',
    });
    deleteButton(quickQuestion.id, quickQuestion.pageId, button);
  };

  const smsOptinYesCase = button.type === ButtonType.OPT_IN && smsOptinMode;

  const isLeadGeneratorRoute = useIsLeadGeneratorBuilderRoute();
  const dialogueClasses = classnames(
    'question-button-active-tab-area__associated-dialogue',
    {
      'question-button-active-tab-area__associated-dialogue--disabled':
        smsOptinYesCase,
    },
  );
  const containerClasses = classnames(
    'question-button-active-tab-area__active-tab-area-top',
    {
      'question-button-active-tab-area__active-tab-area-top--wrap':
        smsOptinMode,
    },
  );

  const modalActions = useModalActions();

  const onDialogueClick = () => {
    if (smsOptinYesCase) {
      return;
    }

    modalActions.openDialoguePickerNewModal({
      selectedDialogueId: pageId,
      withNoOption: true,
      onDialogueSelected: (_, dialogue) => {
        onDropDownChange(dialogue as DropdownOption);
        successToast('Dialogue updated');
      },
    });
  };

  const onCreateClick = () => {
    onDialogueClick();
  };

  const label = useMemo<JSX.Element>(
    () => (
      <>
        If user clicks{' '}
        <strong>{localButtonName.length ? localButtonName : ''}</strong> button
        then send...
      </>
    ),
    [],
  );

  return (
    <div className="question-button-active-tab-area">
      <CloseCircleIcon
        color={colors.gray}
        className="question-button__close-icon"
        onClick={() => setSelectedButton(undefined)}
      />

      <Heading size={4} isBold={false}>
        Button Text
      </Heading>

      <div className={containerClasses}>
        {!smsOptinMode && (
          <QuestionButtonImageUpload
            quickQuestion={quickQuestion}
            button={button}
          />
        )}

        {
          <>
            <TextInput
              maxLength={MAX_INPUT_LENGTH}
              inputRef={memoizedSetInputRef}
              value={localButtonName}
              disabled={smsOptinMode}
              onChange={onInputChange}
              onBlur={onInputBlur}
              className={classnames(
                'question-button-active-tab-area__name-input',
                {
                  'question-button-active-tab-area__name-input--disabled':
                    smsOptinMode,
                },
              )}
              placeholder="Enter answer"
            />
            <EmojiPicker
              disabled={
                smsOptinMode || localButtonName.length >= MAX_INPUT_LENGTH
              }
              onEmojiPicked={updateInsertEmojiQuestion}
              className="question-button-active-tab-area__emoji-picker"
            />
          </>
        }
      </div>

      {!isLeadGeneratorRoute && !(kind === 'sms' && !smsActivated) && (
        <DialogueSelectForm
          dialogueId={pageId}
          dialogueName={selectedPage?.name}
          label={label}
          disabled={smsOptinYesCase}
          onChangeClick={onDialogueClick}
          onCreateClick={onCreateClick}
        />
      )}

      {kind === 'sms' && (
        <>
          {smsActivated ? (
            <div className="question-button-active-tab-area__sms-response-area">
              <div className="question-button-active-tab-area-input-heading">
                SMS opt-in confirmation
                <PrimaryToolTip
                  classNamePopoverContainer="question-button-active-tab-area__tooltip-popover"
                  anchorElement={<i className="far fa-question-circle" />}
                >
                  SMS message to be sent to user’s phone after they opt in
                </PrimaryToolTip>
              </div>
              <InputWithEmoji
                className="question-button-active-tab-area__sms-response-input-wrapper"
                textInputClassName="question-button-active-tab-area__sms-response-input"
                multiline
                value={smsResponse}
                onChange={setSmsResponse}
                onBlur={onSmsInputBlur}
              />
              <Hint className="question-button-active-tab-area__input-hint">
                IMPORTANT: Opt-in confirmation won't be sent until the contact
                provides a valid phone number. See Phone Number Confirmation
                dialogue.
              </Hint>
            </div>
          ) : (
            <div className="question-button-active-tab-area__sms-not-activated-alert">
              <RedInfoIcon className="question-button-active-tab-area__sms-not-activated-alert-icon" />
              Phone number is not activated.&nbsp;
              <Link
                to={BotBuilderRoutes.settingsSmsActivate.getLink({ chatbotId })}
              >
                Activate SMS
              </Link>
            </div>
          )}
          <div className="question-button-active-tab-area__tags-wrapper">
            <ButtonTagsForm
              title="Add a Tag to contacts who click this button"
              kind="add_tags"
              tags={tags}
              onTagAdd={(tag: TagModel) =>
                updateTags(button, Array.from(new Set(tags.concat([tag]))))
              }
              onTagRemove={(tag: TagModel) =>
                updateTags(
                  button,
                  tags.filter(t => t.name !== tag.name),
                )
              }
            />
          </div>
        </>
      )}

      <div className="question-button-active-tab-area__bottom">
        {!smsOptinMode && (
          <span className="question-button-active-tab-area__delete">
            <Button
              color="gray"
              onClick={onClickDelete}
              leftIcon={
                <TrashIcon
                  className="question-button-active-tab-area__trash-icon"
                  color={colors.red}
                />
              }
            />
          </span>
        )}
      </div>
    </div>
  );
};
